import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import Button from "../../../UI/Button/Button";
import CheckBox from "../../../UI/Button/CheckboxButton";
import ThresholdsCard from "./ThresholdsCard";
import ErrorPanel, { ErrorSize } from "../../../UI/ErrorPanel/ErrorPanel";

import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import * as AnalyticsActions from "../../../../middleware/Store/Actions/AnalyticsActions";
import * as ThresholdConstant from "../../../../middleware/Constants/ThresholdsConstant";
import * as ThresholdUtils from "../../../Utils/ThresholdUtils";

import LogService from "../../../../middleware/CommonSdk/LogService";
const LOG_TAG = "THRESHOLD_LIST";

const ThresholdList = (props) => {

    const intl = useIntl();
    const [userUpdate, setUserUpdate] = useState(false);
    const [thresholds, setThresholds] = useState(props.thresholds);
    const [counterValues, setCounterValues] = useState(null);
    const [progress, setProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect groupsMapping, analytics");

        if (props.groupsMapping) {
            const counters = ThresholdUtils.consolidateThresholds(props.analytics, props.groupsMapping);
            setCounterValues(counters);
        }
    }, [props.groupsMapping, props.analytics]);

    //Delete custom thresholds
    const handleDeleteCustomThreshold = (groupName) => {
        LogService.trace(LOG_TAG, "handleDeleteCustomThreshold", "enter");
        const thresholdArrayCopy = [...thresholds];
        const foundIndex = thresholdArrayCopy.findIndex(item => item.groupName === groupName);

        if (foundIndex >= 0) {
            let modifiedThresholdGroup = { ...thresholdArrayCopy[foundIndex] };
            let freeThreshold = ThresholdUtils.getFreeThreshold(modifiedThresholdGroup);
            let customThreshold = ThresholdUtils.getCustomThreshold(modifiedThresholdGroup) || ThresholdUtils.getEmptyCustomThreshold();

            LogService.trace(LOG_TAG, "handleDeleteCustomThreshold", "customThreshold=" + JSON.stringify(customThreshold));
            customThreshold.threshold = 0;
            customThreshold.notification = ThresholdConstant.ThresholdNotification.None;

            modifiedThresholdGroup.thresholds = [
                freeThreshold,
                customThreshold
            ];
            thresholdArrayCopy[foundIndex] = modifiedThresholdGroup;
        }

        LogService.trace(LOG_TAG, "handleDeleteCustomThreshold", "setdata=" + JSON.stringify(thresholdArrayCopy));
        setUserUpdate(true);
        setThresholds(thresholdArrayCopy);
    }

    //Change the input values
    const handleInput = (event, groupName) => {
        LogService.trace(LOG_TAG, "handleInput", "groupName=" + groupName);
        const thresholdArrayCopy = [...thresholds];
        const foundIndex = thresholdArrayCopy.findIndex(item => item.groupName === groupName);

        if (foundIndex >= 0) {
            let modifiedThresholdGroup = { ...thresholdArrayCopy[foundIndex] };
            let freeThreshold = ThresholdUtils.getFreeThreshold(modifiedThresholdGroup);
            let customThreshold = ThresholdUtils.getCustomThreshold(modifiedThresholdGroup) || ThresholdUtils.getEmptyCustomThreshold();

            customThreshold.threshold = Number.parseInt(event.target.value);
            customThreshold.notification = ThresholdConstant.ThresholdNotification.None;

            modifiedThresholdGroup.thresholds = [
                freeThreshold,
                customThreshold
            ];

            thresholdArrayCopy[foundIndex] = modifiedThresholdGroup;
        }

        setUserUpdate(true);
        setThresholds(thresholdArrayCopy);
    }

    //Change the Email notification
    const handleNotification = (isChecked, groupName, isCustom) => {
        LogService.trace(LOG_TAG, "handleNotification", "enter");
        LogService.trace(LOG_TAG, "handleNotification", "isChecked=" + isChecked);
        const thresholdArrayCopy = [...thresholds];
        const foundIndex = thresholdArrayCopy.findIndex(item => item.groupName === groupName);

        if (foundIndex >= 0) {
            let modifiedThresholdGroup = { ...thresholdArrayCopy[foundIndex] };
            let freeThreshold = ThresholdUtils.getFreeThreshold(modifiedThresholdGroup);
            let customThreshold = ThresholdUtils.getCustomThreshold(modifiedThresholdGroup) || ThresholdUtils.getEmptyCustomThreshold();

            if (isCustom) {
                customThreshold.notification = isChecked ? ThresholdConstant.ThresholdNotification.Mail : ThresholdConstant.ThresholdNotification.None;
            } else if (freeThreshold) {
                freeThreshold.notification = isChecked ? ThresholdConstant.ThresholdNotification.Mail : ThresholdConstant.ThresholdNotification.None;
            }

            modifiedThresholdGroup.thresholds = [
                freeThreshold,
                customThreshold
            ];
            thresholdArrayCopy[foundIndex] = modifiedThresholdGroup;
        }

        setUserUpdate(true);
        setThresholds(thresholdArrayCopy);
    }

    //Update all page
    const handleUpdate = () => {
        LogService.trace(LOG_TAG, "handleUpdate", "enter");

        setProgress(true);
        setErrorMessage("");
        const promiseArray = [];

        thresholds.forEach(item => {
            let freeThreshold = ThresholdUtils.getFreeThreshold(item);
            let customThreshold = ThresholdUtils.getCustomThreshold(item);

            //free
            if (freeThreshold) {
                LogService.trace(LOG_TAG, "handleUpdate", "update free threshold");
                promiseArray.push(props.updateThresholds(props.appId, item.groupName, freeThreshold.type, freeThreshold.notification, freeThreshold.threshold));
            }

            //custom
            const doesCustomExistOnServer = ThresholdUtils.isCustomThresholdAvailableOnServer(item.groupName, props.thresholds); //check on server values
            if (customThreshold && doesCustomExistOnServer && customThreshold.threshold === 0) {
                LogService.trace(LOG_TAG, "handleUpdate", "delete custom threshold");
                promiseArray.push(props.deleteThresholds(props.appId, item.groupName, ThresholdConstant.ThresholdType.Custom));
            } else if (customThreshold && doesCustomExistOnServer) {
                LogService.trace(LOG_TAG, "handleUpdate", "update custom threshold");
                promiseArray.push(props.updateThresholds(props.appId, item.groupName, customThreshold.type, customThreshold.notification, customThreshold.threshold));
            } else if (customThreshold && !doesCustomExistOnServer && customThreshold.threshold !== 0) {
                LogService.trace(LOG_TAG, "handleUpdate", "add custom threshold");
                promiseArray.push(props.addThresholds(props.appId, item.groupName, customThreshold.notification, customThreshold.threshold));
            }
        });

        return Promise.allSettled(promiseArray)
            .then(results => {
                LogService.info(LOG_TAG, "handleUpdate success");
                results.forEach((result) => {
                    if (result.status !== "fulfilled") {
                        let errorPayload = result.reason;
                        LogService.error(LOG_TAG, "handleUpdate error", errorPayload);
                        setErrorMessage(errorPayload.errorMessage);
                    }
                });

                //refresh thresholds values
                let refreshThresholds = Promise.resolve([]);
                if (props.getThresholds)
                    refreshThresholds = props.getThresholds(props.appId);

                return refreshThresholds;
            })
            .then(() => {
                LogService.info(LOG_TAG, "handleUpdate success");
                setProgress(false);
                setUserUpdate(false);
            })
            .catch(errorPayload => {
                LogService.error(LOG_TAG, "handleUpdate error", errorPayload);
                setProgress(false);
                setUserUpdate(false);
                setErrorMessage(errorPayload.errorMessage);
            });
    }

    let renderThreshold = (group, counter) => {
        const canChange = props.canEdit;

        const freeThreshold = ThresholdUtils.getFreeThreshold(group);
        let customThreshold = ThresholdUtils.getCustomThreshold(group) || ThresholdUtils.getEmptyCustomThreshold();

        const disableCustomThreshold = !canChange || customThreshold.threshold <= 0 || Number.isNaN(customThreshold.threshold);

        const value = counter ? counter : 0;
        const exceeded = freeThreshold && freeThreshold.threshold < value;

        const groupName = group.groupName;
        const unit = group.unit;

        return (
            <div id={"thresholds-" + groupName} key={"thr-" + groupName}>

                <h6 className="panel-lineTitle mb-3 mt-4">
                    <span>{intl.formatMessage({ id: "thresholdsFor" }, { group: groupName })}</span>
                </h6>

                <div className="row align-items-center">
                    <div className="col-md-4"><ThresholdsCard value={value} unit={unit} groupName={groupName} exceeded={exceeded} /></div>
                    <div className="col-md-8">
                        <div className="form-group">
                            <label className="input-label">{intl.messages.thresholdsFreeLabel}</label>
                            <div className="input-group mb-1">
                                <div className="form-control" disabled >{freeThreshold.threshold}</div>
                                <span className="input-group-text">{unit}</span>
                            </div>
                            <CheckBox
                                id={"free-checkbox-" + groupName}
                                type="checkbox"
                                label={intl.messages.thresholdsEmail}
                                disabled={!canChange || progress}
                                checked={freeThreshold.notification === ThresholdConstant.ThresholdNotification.Mail}
                                onClick={(isChecked) => handleNotification(isChecked, groupName, false)}
                            />
                        </div>

                        <div className="form-group">
                            <label className="input-label">{intl.messages.thresholdsCustomLabel}</label>
                            <div className="input-group mb-1">
                                <input id={"custom-val-" + groupName}
                                    type="number" min="0"
                                    className="form-control"
                                    aria-label="Custom value and Unit threshold"
                                    value={customThreshold.threshold}
                                    disabled={!canChange || progress}
                                    onChange={event => handleInput(event, groupName)}
                                />

                                <span className="input-group-text">{unit}</span>
                                {!disableCustomThreshold &&
                                    <div className="input-group-append">
                                        <button id={"custom-delete-" + groupName}
                                            className="btn input-btn" type="button"
                                            disabled={!canChange || progress}
                                            onClick={() => handleDeleteCustomThreshold(groupName)}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} />
                                        </button>
                                    </div>
                                }
                            </div>
                            <CheckBox
                                id={"custom-checkbox-" + groupName}
                                type="checkbox"
                                label={intl.messages.thresholdsEmail}
                                disabled={disableCustomThreshold}
                                checked={customThreshold.notification === ThresholdConstant.ThresholdNotification.Mail}
                                onClick={(isChecked) => handleNotification(isChecked, groupName, true)}
                            />
                        </div>

                        <div className="panel bg-fog mb-3">
                            {intl.formatMessage({ id: "thresholdsUpdateMessage" }, { b: chunks => <b>{chunks}</b> })}
                        </div>
                    </div>
                </div>


            </div>
        )
    };

    let displayThresholds = thresholds.map(item => {
        let counters = 0;
        if (counterValues)
            counters = counterValues[item.groupName];
        return renderThreshold(item, counters);
    });

    return (
        <div>
            {displayThresholds}

            {props.canEdit && <div>
                <div className="mt-3">
                    <Button
                        id="application-threshold-update-button"
                        label={intl.messages.update}
                        icon={faEdit}
                        disabled={!props.canEdit || !userUpdate}
                        onClick={() => handleUpdate()}
                        spinner={progress}
                        className="btn-submit"
                    />
                    {errorMessage &&
                        <div className="col-10 float-right">
                            <ErrorPanel size={ErrorSize.Small} >{errorMessage}</ErrorPanel>
                        </div>
                    }
                </div>
            </div>
            }
        </div>
    )
}

ThresholdList.defaultProps = {
    appId: null,
    canEdit: false,
    thresholds: [],
    analytics: []
}

ThresholdList.propTypes = {
    appId: PropTypes.string,
    canEdit: PropTypes.bool,
    thresholds: PropTypes.array,
    analytics: PropTypes.array,
    getThresholds: PropTypes.func,
    groupsMapping: PropTypes.object
};

const mapDispatchToProps = dispatch => {
    return {
        updateThresholds: (appId, groupName, type, notif, value) => dispatch(AnalyticsActions.updateThreshold(appId, groupName, type, notif, value)),
        addThresholds: (appId, groupName, notif, value) => dispatch(AnalyticsActions.addThreshold(appId, groupName, notif, value)),
        deleteThresholds: (appId, groupName, type) => dispatch(AnalyticsActions.deleteThreshold(appId, groupName, type)),
    }
}

export default connect(null, mapDispatchToProps)(ThresholdList);